import { defineStore } from 'pinia'

import * as invoiceRequestApi from '../services/api/invoice-request'
import { IApproval } from './staff'

export interface IInvoiceRequestFile {
  business_registration: boolean
  tax_code: boolean
  family_register: boolean
  identity: boolean
}

export interface IInvoiceRequestData {
  id: number
  status: string
  department: string
  is_urgent: boolean
  note: string
  reason: string
  approvals: Array<IApproval>
  sell_contract_no: string
  sell_contract_signed_at: string
  final_price: number
  invoice_price: number
  paid_amount: number
  remain_amount: number
  method: string
  files: IInvoiceRequestFile
  sell_request_no: string
  sell_request_signed_at: string
  sell_request_price: number
  sell_request_images: Array<any>
  economic_contract_no: string
  economic_contract_signed_at: string
  economic_contract_price: number
  economic_contract_images: Array<any>
  assignee_id: number
  car_id: number
  customer_id: number
  assignee: any
  car: any
  customer: any
  created_at: string
}

export const useInvoiceRequestStore = defineStore('invoiceRequest', {
  state: () => ({
    invoiceRequest: {
      id: 0,
      status: '',
      department: '',
      is_urgent: false,
      note: '',
      reason: '',
      approvals: [],
      sell_contract_no: '',
      sell_contract_signed_at: '',
      final_price: 0,
      invoice_price: 0,
      paid_amount: 0,
      remain_amount: 0,
      method: '',
      files: {
        business_registration: false,
        tax_code: false,
        family_register: false,
        identity: false,
      },
      sell_request_no: '',
      sell_request_signed_at: '',
      sell_request_price: 0,
      sell_request_images: [],
      economic_contract_no: '',
      economic_contract_signed_at: '',
      economic_contract_price: 0,
      economic_contract_images: [],
      assignee_id: 0,
      car_id: 0,
      customer_id: 0,
      assignee: {},
      car: {},
      customer: {},
      created_at: '',
    } as IInvoiceRequestData,
    isLoading: false,
  }),

  actions: {
    setInvoiceRequest(data: IInvoiceRequestData) {
      this.invoiceRequest = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getInvoiceRequest(id: number) {
      try {
        this.setLoading(true)
        const { data } = await invoiceRequestApi.get(id)
        this.setInvoiceRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateInvoiceRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await invoiceRequestApi.update(this.invoiceRequest.id, payload)
        this.setInvoiceRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async exportDocxInvoiceRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await invoiceRequestApi.exportDocx(this.invoiceRequest.id, payload)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applyInvoiceRequest() {
      try {
        this.setLoading(true)
        const { data } = await invoiceRequestApi.apply(this.invoiceRequest.id)
        this.setInvoiceRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectInvoiceRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await invoiceRequestApi.reject(this.invoiceRequest.id, payload)
        this.setInvoiceRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeInvoiceRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await invoiceRequestApi.remake(this.invoiceRequest.id, payload)
        this.setInvoiceRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveInvoiceRequest() {
      try {
        this.setLoading(true)
        const { data } = await invoiceRequestApi.approve(this.invoiceRequest.id)
        this.setInvoiceRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useInvoiceRequestListStore = defineStore('invoiceRequestList', {
  state: () => ({
    invoiceRequests: [] as IInvoiceRequestData[],
    count: 0,
    isLoading: false,
    total: 0,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    addInvoiceRequest(data: IInvoiceRequestData) {
      this.invoiceRequests.push(data)
    },

    setInvoiceRequests(data: IInvoiceRequestData[]) {
      this.invoiceRequests = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    setTotal(value: number) {
      this.total = value
    },

    async countInvoiceRequests() {
      try {
        this.setLoading(true)
        const { data } = await invoiceRequestApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async createInvoiceRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await invoiceRequestApi.create(payload)
        this.addInvoiceRequest(data)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listInvoiceRequests(params: any) {
      try {
        this.setLoading(true)
        const result = await invoiceRequestApi.list(params)
        const { data, total } = result as any
        this.setInvoiceRequests(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllInvoiceRequests(params: any) {
      try {
        this.setLoading(true)
        const result = await invoiceRequestApi.listAll(params)
        const { data, total } = result as any
        this.setInvoiceRequests(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
